import PropTypes from 'prop-types';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import FJLocalStore from '../../../../store/FJLocalStore.js';
import {
    AI_GENERATOR_VIDEO_TAB_TYPE,
    AI_VIDEO_GENERATION_MODEL_LIST,
    MODEL_VALUE_MAP,
} from '../../../../config/dataConfig/FJAIVideoGeneratorData.js';
import FJVerticalScroller from '../../../../common/components/FJScroller/FJVerticalScroller.jsx';
import FJCloseIcon from '../../../../assets/svg/close.svg?react';
import FJAIVideoModelPreviewItem from './FJAIVideoModelPreviewItem.jsx';
import FJTextWithEllipsisParent from '../../../../common/components/FJTextWithEllipsis/FJTextWithEllipsisParent.jsx';

import styles from './FJAIVideoModelPicker.module.less';
import FJUtil from '../../../../util/FJUtil.js';

const FJAIVideoModelPicker = ({ currentModel, onChange, isVisible, onToggle, tab }) => {
    const [internalVisible, setInternalVisible] = useState(false);
    const [animating, setAnimating] = useState(false);
    const timeoutRef = useRef(null);
    const modelPickerRef = useRef(null);

    const handleModelChange = useCallback(
        modelValue => {
            onChange?.(modelValue);
            onToggle?.(false);
        },
        [onChange, onToggle],
    );

    const handleClose = useCallback(() => {
        onToggle?.(false);
    }, [onToggle]);

    const handleOutsideClick = useCallback(
        e => {
            const clickedElement = e.target;
            // 检查是否点击在组件内部
            if (!modelPickerRef.current?.contains(clickedElement) && isVisible) {
                // 检查是否点击在trigger按钮上（通过查找具有特定属性或类名的元素）
                const isClickOnTrigger = clickedElement.closest('[data-model-picker-trigger]');
                if (!isClickOnTrigger) {
                    handleClose();
                }
            }
        },
        [handleClose, isVisible],
    );

    useEffect(() => {
        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [handleOutsideClick]);

    useEffect(() => {
        if (isVisible) {
            // 显示：立即设置为可见，然后触发渐入动画
            setInternalVisible(true);
            setAnimating(true);
            // selected model scroll into view
            const selectedModel = modelPickerRef.current?.querySelector(`[data-model="${currentModel}"]`);
            if (selectedModel && !FJUtil.isLandingPage()) {
                selectedModel.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
            }
        } else {
            // 隐藏：先触发渐出动画，然后隐藏元素
            if (internalVisible) {
                setAnimating(false);
                timeoutRef.current = setTimeout(() => {
                    setInternalVisible(false);
                }, 300); // 与CSS动画时长一致
            }
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [isVisible, internalVisible, currentModel]);

    // REQUIREMENT: 拆分MODEL_LIST为auto和other两组 供UI显示，根据tab类型拆分，且替换部分文案
    const groupedModelList = useMemo(() => {
        if (tab === AI_GENERATOR_VIDEO_TAB_TYPE.AI_IMAGE_TO_VIDEO) {
            const imageToVideoModelList = AI_VIDEO_GENERATION_MODEL_LIST.filter(model =>
                model.supportedModes.includes(tab),
            );
            return [imageToVideoModelList.slice(0, 3), imageToVideoModelList.slice(3)];
        } else {
            // REQUIREMENT: 替换部分文案
            const textToVideoModelList = AI_VIDEO_GENERATION_MODEL_LIST.filter(model =>
                model.supportedModes.includes(tab),
            ).map(model => {
                return model.value === MODEL_VALUE_MAP.KLING_16_PRO
                    ? { ...model, description: '1080p-output-with-refined-motion-logic-and-effects' }
                    : model;
            });
            return [textToVideoModelList.slice(0, 3), textToVideoModelList.slice(3)];
        }
    }, [tab]);

    // 如果组件不可见，则不渲染
    if (FJUtil.getABTest() || tab === AI_GENERATOR_VIDEO_TAB_TYPE.AI_EFFECTS_VIDEO) {
        return null;
    }

    return (
        <div
            className={styles.overlay}
            data-active={animating}
            ref={modelPickerRef}
            style={{ display: internalVisible ? 'flex' : 'none' }}
        >
            <div className={styles.header}>
                <FJTextWithEllipsisParent className={styles.title} text={FJLocalStore._('pick-model-title')} />
                <div className={styles.close_icon} onClick={handleClose}>
                    <FJCloseIcon />
                </div>
            </div>
            <FJVerticalScroller className={styles.scroll}>
                <div className={styles.header_title}>FlexClip</div>
                <div className={styles.model_list}>
                    {groupedModelList[0]
                        .filter(model => model.supportedModes.includes(tab))
                        .map((model, index) => (
                            <FJAIVideoModelPreviewItem
                                key={model.value + index}
                                model={model}
                                currentModel={currentModel}
                                onModelSelect={handleModelChange}
                            />
                        ))}
                </div>
                <div className={styles.header_title}>{FJLocalStore._('other-model')}</div>
                <div className={styles.model_list}>
                    {groupedModelList[1]
                        .filter(model => model.supportedModes.includes(tab))
                        .map((model, index) => (
                            <FJAIVideoModelPreviewItem
                                key={model.value + index}
                                model={model}
                                currentModel={currentModel}
                                onModelSelect={handleModelChange}
                            />
                        ))}
                </div>
            </FJVerticalScroller>
        </div>
    );
};

FJAIVideoModelPicker.propTypes = {
    currentModel: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    modelList: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
            description: PropTypes.string.isRequired,
        }),
    ),
    onToggle: PropTypes.func,
    isVisible: PropTypes.bool,
    tab: PropTypes.string,
};

export default FJAIVideoModelPicker;
